import { AuditLogAction } from "@/core";
import { STAGE_DEFINITIONS } from "@/utils/dealStage";
import { format, intervalToDuration } from "date-fns";
import {
  Edit3,
  FileText,
  Minus,
  Plus,
  Trash2,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import type { ReactNode } from "react";

export function getActionIcon(action: AuditLogAction) {
  switch (action) {
    case AuditLogAction.CREATE:
      return <Plus className="h-4 w-4 text-green-600" />;
    case AuditLogAction.UPDATE:
      return <Edit3 className="h-4 w-4 text-blue-400" />;
    case AuditLogAction.DELETE:
      return <Trash2 className="h-4 w-4 text-red-600" />;
  }
}

export function getActionColor(action: AuditLogAction) {
  switch (action) {
    case AuditLogAction.CREATE:
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    case AuditLogAction.UPDATE:
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue";
    case AuditLogAction.DELETE:
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
  }
}

export function getChangeIcon(fromValue: unknown, toValue: unknown) {
  // Handle numeric comparisons
  if (typeof fromValue === "number" && typeof toValue === "number") {
    if (toValue > fromValue) {
      return <TrendingUp className="h-4 w-4 text-green-600" />;
    } else if (toValue < fromValue) {
      return <TrendingDown className="h-4 w-4 text-red-600" />;
    }
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  }

  // Handle array length comparisons
  if (Array.isArray(fromValue) && Array.isArray(toValue)) {
    if (toValue.length > fromValue.length) {
      return <TrendingUp className="h-4 w-4 text-green-600" />;
    } else if (toValue.length < fromValue.length) {
      return <TrendingDown className="h-4 w-4 text-red-600" />;
    }
  }

  // Handle nested object comparisons (like price changes)
  if (
    typeof fromValue === "object" &&
    typeof toValue === "object" &&
    fromValue &&
    toValue
  ) {
    // For price objects, compare the price field
    if ("price" in fromValue && "price" in toValue) {
      const fromPrice = (fromValue as Record<string, unknown>).price;
      const toPrice = (toValue as Record<string, unknown>).price;
      if (typeof fromPrice === "number" && typeof toPrice === "number") {
        if (toPrice > fromPrice) {
          return <TrendingUp className="h-4 w-4 text-green-600" />;
        } else if (toPrice < fromPrice) {
          return <TrendingDown className="h-4 w-4 text-red-600" />;
        }
      }
    }

    // For percentage objects
    if ("percentage" in fromValue && "percentage" in toValue) {
      const fromPercentage = (fromValue as Record<string, unknown>).percentage;
      const toPercentage = (toValue as Record<string, unknown>).percentage;
      if (
        typeof fromPercentage === "number" &&
        typeof toPercentage === "number"
      ) {
        if (toPercentage > fromPercentage) {
          return <TrendingUp className="h-4 w-4 text-green-600" />;
        } else if (toPercentage < fromPercentage) {
          return <TrendingDown className="h-4 w-4 text-red-600" />;
        }
      }
    }
  }

  return <FileText className="h-4 w-4 text-blue-600" />;
}

export function humanizeFieldName(fieldName: string): string {
  return fieldName
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

function getTimestampSeconds(value: object): number | null {
  const record = value as Record<string, unknown>;
  if (typeof record.seconds === "number") {
    return record.seconds;
  }
  if (typeof record._seconds === "number") {
    return record._seconds;
  }
  return null;
}

export function formatValue(value: unknown, fieldName?: string): string {
  if (value === null || value === undefined) {
    if (fieldName) {
      return `No ${humanizeFieldName(fieldName).toLowerCase()} history`;
    }
    return "null";
  }
  if (typeof value === "object" && value !== null) {
    const timestampSeconds = getTimestampSeconds(value);
    if (timestampSeconds !== null) {
      return format(new Date(timestampSeconds * 1000), "MMM dd, yyyy HH:mm:ss");
    }
    // Legacy data shape: arrayUnion sentinels written before the FieldValue
    // guard landed in removeUndefinedValues were persisted as { elements: [...] }.
    // Treat them as the array they were meant to be.
    const record = value as Record<string, unknown>;
    if (
      Object.keys(record).length === 1 &&
      Array.isArray(record.elements)
    ) {
      return formatValue(record.elements, fieldName);
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return "(empty)";
      }
      const allPrimitive = value.every(
        (item) =>
          item === null ||
          typeof item === "string" ||
          typeof item === "number" ||
          typeof item === "boolean",
      );
      if (allPrimitive) {
        return value.map((item) => String(item)).join(", ");
      }
      return JSON.stringify(value, null, 2);
    }
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

interface ChangeItem {
  key: string | number;
  changes: Record<string, { from: unknown; to: unknown }>;
  fromItem: Record<string, unknown>;
  toItem: Record<string, unknown>;
}

const STAGE_DEFINITIONS_LIST = Object.values(STAGE_DEFINITIONS);

function getStageNameFromId(stageId: unknown): string {
  if (stageId === null || stageId === undefined) {
    return "Unknown stage";
  }

  const stageIdString = String(stageId);
  const definition = STAGE_DEFINITIONS_LIST.find(
    (stage) => stage.value === stageIdString,
  );

  if (definition) {
    return `${definition.displayName} (${stageIdString})`;
  }

  return stageIdString;
}

function formatDurationFromMilliseconds(duration: unknown): string {
  if (typeof duration !== "number" || Number.isNaN(duration) || duration < 0) {
    return "--";
  }

  if (duration === 0) {
    return "0m";
  }

  const {
    days = 0,
    hours = 0,
    minutes = 0,
  } = intervalToDuration({
    start: 0,
    end: duration,
  });

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  return `${minutes}m`;
}

function renderStageDurations(stageDurations: unknown): ReactNode {
  if (!Array.isArray(stageDurations)) {
    return null;
  }

  if (stageDurations.length === 0) {
    return (
      <div className="italic text-muted-foreground">
        No recorded stage durations
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {stageDurations.map((entry, index) => {
        if (!entry || typeof entry !== "object") {
          return (
            <div
              key={index}
              className="rounded border border-border/30 bg-background/20 p-2 text-muted-foreground"
            >
              Unable to display duration entry
            </div>
          );
        }

        const durationEntry = entry as Record<string, unknown>;
        const stageName = getStageNameFromId(durationEntry.stage);
        const durationText = formatDurationFromMilliseconds(
          durationEntry.durationMs,
        );
        const enteredAt = durationEntry.enteredAt
          ? formatValue(durationEntry.enteredAt)
          : null;
        const exitedAt = durationEntry.exitedAt
          ? formatValue(durationEntry.exitedAt)
          : null;

        return (
          <div
            key={`${stageName}-${index}`}
            className="rounded border border-border/30 bg-background/20 p-2 space-y-1"
          >
            <div className="text-sm font-medium text-foreground dark:text-white">
              {stageName}
            </div>
            {enteredAt && (
              <div className="text-[11px] text-muted-foreground">
                Entered: {enteredAt}
              </div>
            )}
            {exitedAt && (
              <div className="text-[11px] text-muted-foreground">
                Exited: {exitedAt}
              </div>
            )}
            {durationText !== "--" && (
              <div className="text-[11px] text-muted-foreground">
                Duration: {durationText}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function renderStageHistorySnapshot(stageHistory: unknown): ReactNode {
  if (!stageHistory) {
    return <div className="italic text-muted-foreground">No stage history</div>;
  }

  if (typeof stageHistory !== "object") {
    return <div>{formatValue(stageHistory)}</div>;
  }

  const history = stageHistory as Record<string, unknown>;
  const sections: ReactNode[] = [];

  if (history.currentStage !== undefined) {
    sections.push(
      <div key="currentStage" className="space-y-1">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Current Stage
        </div>
        <div className="text-sm font-medium text-foreground dark:text-white">
          {getStageNameFromId(history.currentStage)}
        </div>
      </div>,
    );
  }

  if (history.currentStageEnteredAt) {
    sections.push(
      <div key="currentStageEnteredAt" className="space-y-1">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Current Stage Entered
        </div>
        <div className="text-sm text-foreground dark:text-white">
          {formatValue(history.currentStageEnteredAt)}
        </div>
      </div>,
    );
  }

  if (history.totalTimeInPipeline !== undefined) {
    sections.push(
      <div key="totalTimeInPipeline" className="space-y-1">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Total Time In Pipeline
        </div>
        <div className="text-sm text-foreground dark:text-white">
          {formatDurationFromMilliseconds(history.totalTimeInPipeline)}
        </div>
      </div>,
    );
  }

  if (history.stageDurations !== undefined) {
    sections.push(
      <div key="stageDurations" className="space-y-1">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Stage Durations
        </div>
        {renderStageDurations(history.stageDurations)}
      </div>,
    );
  }

  if (sections.length === 0) {
    return (
      <div className="italic text-muted-foreground">
        No stage history details
      </div>
    );
  }

  return <div className="space-y-2">{sections}</div>;
}

function renderStageHistoryChange(from: unknown, to: unknown): ReactNode {
  return (
    <div className="grid grid-cols-1 gap-2">
      <div className="bg-green-50 dark:bg-green-900/20 px-4 py-2 rounded-sm text-sm flex gap-2">
        <div className="w-[30%]">
          <span className="text-green-400">Updated</span>
        </div>
        <div className="text-green-300">{renderStageHistorySnapshot(to)}</div>
      </div>
      <div className="px-4 py-2 rounded-sm text-sm bg-slate-50 dark:bg-slate-900/20 flex gap-2">
        <div className="w-[30%]">
          <span className="text-slate-500 dark:text-white/50">Old</span>
        </div>
        <div className="text-slate-500 dark:text-white/50">
          {renderStageHistorySnapshot(from)}
        </div>
      </div>
    </div>
  );
}

export function findChangedItems(
  fromArray: Record<string, unknown>[],
  toArray: Record<string, unknown>[],
): {
  changed: ChangeItem[];
  added: Record<string, unknown>[];
  removed: Record<string, unknown>[];
} {
  const changed: ChangeItem[] = [];
  const added: Record<string, unknown>[] = [];
  const removed: Record<string, unknown>[] = [];

  // Create a map for efficient lookup by a unique identifier
  const fromMap = new Map();
  const toMap = new Map();

  // For objects with identifiable fields, use them as keys
  fromArray.forEach((item, index) => {
    const key = item.name || item.id || index; // Use name, id, or index as key
    fromMap.set(key, item);
  });

  toArray.forEach((item, index) => {
    const key = item.name || item.id || index;
    toMap.set(key, item);
  });

  // Find changed items by comparing matching keys
  fromMap.forEach((fromItem, key) => {
    const toItem = toMap.get(key);
    if (toItem) {
      // Deep compare each field to find actual changes
      const itemChanges: Record<string, { from: unknown; to: unknown }> = {};
      let hasChanges = false;

      Object.keys(fromItem).forEach((field) => {
        if (fromItem[field] !== toItem[field]) {
          itemChanges[field] = { from: fromItem[field], to: toItem[field] };
          hasChanges = true;
        }
      });

      Object.keys(toItem).forEach((field) => {
        if (!(field in fromItem)) {
          itemChanges[field] = { from: undefined, to: toItem[field] };
          hasChanges = true;
        }
      });

      if (hasChanges) {
        changed.push({
          key,
          changes: itemChanges,
          fromItem,
          toItem,
        });
      }
    } else {
      // Item was removed
      removed.push(fromItem);
    }
  });

  // Find added items
  toMap.forEach((toItem, key) => {
    if (!fromMap.has(key)) {
      added.push(toItem);
    }
  });

  return { changed, added, removed };
}

export function renderChangeDetails(
  fieldName: string,
  change: { from?: unknown; to?: unknown },
) {
  const { from, to } = change;

  // Special handling for sellPrices array
  if (fieldName === "sellPrices" && Array.isArray(from) && Array.isArray(to)) {
    const { changed, added, removed } = findChangedItems(from, to);

    return (
      <div className="space-y-3">
        {changed.length > 0 && (
          <div className="space-y-2">
            {changed.map((item, index) => (
              <div key={index} className="space-y-2">
                {Object.entries(item.changes)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(
                    ([field, fieldChange]: [
                      string,
                      { from: unknown; to: unknown },
                    ]) => (
                      <div key={field} className="grid grid-cols-1 gap-2">
                        <div className="bg-green-50 dark:bg-green-900/20 px-4 py-2 rounded-sm text-sm flex">
                          <div className="w-[30%]">
                            <span className="text-green-400">Updated</span>
                          </div>
                          <span className="text-green-300">
                            {String(fieldChange.to)}
                          </span>
                        </div>
                        <div className=" px-4 py-2 rounded-sm text-sm bg-slate-50 dark:bg-slate-900/20 flex">
                          <div className="w-[30%]">
                            <span className="text-slate-500 dark:text-white/50">
                              Old
                            </span>
                          </div>
                          <span className="text-slate-500 dark:text-white/50">
                            {String(fieldChange.from)}
                          </span>
                        </div>
                      </div>
                    ),
                  )}
              </div>
            ))}
          </div>
        )}

        {added.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-medium text-green-600">
              Added Tiers:
            </div>
            {added.map((tier, index) => (
              <div
                key={index}
                className="bg-green-50 dark:bg-green-950/20 p-2 rounded border text-xs"
              >
                <div className="font-medium">${String(tier.price)}</div>
                <div className="text-muted-foreground">
                  Range:{" "}
                  {typeof tier.minRange === "number"
                    ? tier.minRange.toLocaleString()
                    : String(tier.minRange)}{" "}
                  -{" "}
                  {typeof tier.maxRange === "number"
                    ? tier.maxRange.toLocaleString()
                    : String(tier.maxRange)}
                </div>
              </div>
            ))}
          </div>
        )}

        {removed.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-medium text-red-600">
              Removed Tiers:
            </div>
            {removed.map((tier, index) => (
              <div
                key={index}
                className="bg-red-50 dark:bg-red-950/20 p-2 rounded border text-xs"
              >
                <div className="font-medium">${String(tier.price)}</div>
                <div className="text-muted-foreground">
                  Range:{" "}
                  {typeof tier.minRange === "number"
                    ? tier.minRange.toLocaleString()
                    : String(tier.minRange)}{" "}
                  -{" "}
                  {typeof tier.maxRange === "number"
                    ? tier.maxRange.toLocaleString()
                    : String(tier.maxRange)}
                </div>
              </div>
            ))}
          </div>
        )}

        {changed.length === 0 && added.length === 0 && removed.length === 0 && (
          <div className="text-xs text-muted-foreground italic">
            No changes detected
          </div>
        )}
      </div>
    );
  }

  // Special handling for statusTiers array
  if (fieldName === "statusTiers" && Array.isArray(from) && Array.isArray(to)) {
    const { changed, added, removed } = findChangedItems(from, to);

    return (
      <div className="space-y-3">
        {changed.length > 0 && (
          <div className="space-y-2">
            {changed.map((item, index) => (
              <div key={index} className="space-y-2">
                <div className="text-xs font-medium">
                  Tier: {String(item.fromItem.name)}
                </div>
                {Object.entries(item.changes)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(
                    ([field, fieldChange]: [
                      string,
                      { from: unknown; to: unknown },
                    ]) => (
                      <div key={field} className="grid grid-cols-1 gap-2">
                        <div className="bg-green-50 dark:bg-green-900/20 px-4 py-2 rounded-sm text-sm flex">
                          <div className="w-[30%]">
                            <span className="text-green-400">Updated</span>
                          </div>
                          <span className="text-green-300">
                            {field === "percentage"
                              ? `${String(fieldChange.to)}%`
                              : String(fieldChange.to)}
                          </span>
                        </div>
                        <div className="px-4 py-2 rounded-sm text-sm bg-slate-50 dark:bg-slate-900/20 flex">
                          <div className="w-[30%]">
                            <span className="text-slate-500 dark:text-white/50">
                              Old
                            </span>
                          </div>
                          <span className="text-slate-500 dark:text-white/50">
                            {field === "percentage"
                              ? `${String(fieldChange.from)}%`
                              : String(fieldChange.from)}
                          </span>
                        </div>
                      </div>
                    ),
                  )}
              </div>
            ))}
          </div>
        )}

        {added.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-medium text-green-600">
              Added Tiers:
            </div>
            {added.map((tier, index) => (
              <div
                key={index}
                className="bg-green-50 dark:bg-green-950/20 p-2 rounded border text-xs"
              >
                <div className="font-medium">{String(tier.name)}</div>
                <div className="text-muted-foreground">
                  {String(tier.percentage)}% commission
                </div>
              </div>
            ))}
          </div>
        )}

        {removed.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-medium text-red-600">
              Removed Tiers:
            </div>
            {removed.map((tier, index) => (
              <div
                key={index}
                className="bg-red-50 dark:bg-red-950/20 p-2 rounded border text-xs"
              >
                <div className="font-medium">{String(tier.name)}</div>
                <div className="text-muted-foreground">
                  {String(tier.percentage)}% commission
                </div>
              </div>
            ))}
          </div>
        )}

        {changed.length === 0 && added.length === 0 && removed.length === 0 && (
          <div className="text-xs text-muted-foreground italic">
            No changes detected
          </div>
        )}
      </div>
    );
  }

  if (fieldName === "stageHistory") {
    return renderStageHistoryChange(from, to);
  }

  // Special handling for updatedAt timestamps

  // Default handling for simple values
  return (
    <div className="grid grid-cols-1 gap-2">
      <div className="bg-green-50 dark:bg-green-900/20 px-4 py-2 rounded-sm text-sm flex gap-2">
        <div className="w-[30%]">
          <span className="text-green-400">Updated</span>
        </div>
        <span className="text-green-300">{formatValue(to, fieldName)}</span>
      </div>
      <div className="px-4 py-2 rounded-sm text-sm bg-slate-50 dark:bg-slate-900/20 flex gap-2">
        <div className="w-[30%]">
          <span className="text-slate-500 dark:text-white/50">Old</span>
        </div>
        <span className="text-slate-500 dark:text-white/50">
          {formatValue(from, fieldName)}
        </span>
      </div>
    </div>
  );
}

export function formatTimestamp(timestamp: string | Date): string {
  const date = new Date(timestamp);
  return date.toLocaleString();
}
